import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, HostListener, inject } from '@angular/core';
import { Subscription, Observable, of, combineLatest, switchMap, catchError, filter, firstValueFrom, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { AuthService } from './core/authentication/auth.service';
import { NavigationStart, Router } from '@angular/router';
import './common/extensions/string.extensions';
import './common/extensions/date.extensions';
import './common/extensions/array.extensions';
import Hotjar from '@hotjar/browser';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { WorkerService } from './data/worker.service';
import { ModuleName } from './subscription-package';
import { LogoutService } from './shared/services/logout.service';
import { APP_CONFIG } from './app-config.token';
import { AppConfig } from './app-config';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WarningDialogComponent } from './shared/messages/warning-dialog/warning-dialog.component';
import { PageTitleNotification } from './common/utils/page-title-notification';
import { TranslateService } from '@ngx-translate/core';
import { SubSink } from 'SubSink'
import { Permission } from './common/enums';
import { UpdateService } from './core/update.service';
import { CheckForUpdateService } from './core/check-for-update.service';
import { TalentinaService } from './data/talentina.service';

declare const BANK_HOLIDAY_RELATIVE_PATH: boolean;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private matIconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private readonly workerService = inject(WorkerService);
  private readonly logoutService = inject(LogoutService);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private updateService = inject(UpdateService);
  private checkForUpdateService = inject(CheckForUpdateService);
  private config = inject<AppConfig>(APP_CONFIG);
  private talentinaService = inject(TalentinaService);

  public readonly moduleNames = ModuleName;
  bankHolidayRelPath = BANK_HOLIDAY_RELATIVE_PATH ?? '';

  isAuthenticated$ = this.authService.isAuthenticated$;

  /**
   * Emits <c>true</c> when the current user should see the Talentina menu entry.
   * Users with the static <c>Talentina</c> permission always pass; users with the
   * dynamic <c>TalentinaEmployee</c> permission are additionally verified against
   * their active employment via <c>GET /api/talentina/hasAccess</c>.
   */
  readonly canUseTalentina$ = this.authService.isAuthenticated$.pipe(
    distinctUntilChanged(),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        return of(false);
      }
      if (this.authService.hasPermission(Permission.Talentina)) {
        return of(true);
      }
      if (!this.authService.hasPermission(Permission.TalentinaEmployee)) {
        return of(false);
      }
      return this.talentinaService.hasAccess().pipe(catchError(() => of(false)));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly showServiceSection$ = combineLatest([
    this.authService.isAuthenticated$,
    this.canUseTalentina$,
  ]).pipe(
    map(([isAuthenticated, canUseTalentina]) => isAuthenticated && (
      this.authService.hasAnyPermission([
        Permission.ManageAllInternalWorkers,
        Permission.ManageAllExternalWorkersList,
        Permission.ManageMyExternalWorkersList,
        Permission.ViewWorkersList,
        Permission.ViewWorkerAgreementsList,
        Permission.Supervisor,
        Permission.CreateOrder,
        Permission.ManageEmployeesOrder,
        Permission.ViewTimesheetsList,
        Permission.ViewAllExternalWorkersHolidaysList,
        Permission.ViewMyExternalWorkersHolidaysList,
        Permission.ShowAllLegalizations,
        Permission.ShowAllDeclarations,
        Permission.ShowEmployerObjectDeclarations,
        Permission.ManageWorkerDelegations,
        Permission.HRDelegationManagement,
        Permission.AccountingDelegationManagement,
        Permission.AccountingDelegationManagementAllAccess,
        Permission.FullDelegationsManagement,
        Permission.ManageReports,
        Permission.ManageStatistics,
        Permission.ViewAbsencesList,
      ]) || canUseTalentina
    ))
  );

  get isSignOutRequested() {
    return this.authService.isSignOutRequested;
  }

  isAdmin: boolean;
  isCoordinator: boolean;
  hasAccessToRegulationsAndProcedures: boolean;

  private subs = new SubSink();
  private readonly qrCodesUrl = '/qrCode';
  isAdminSubscription: Subscription;
  isDesktopView: boolean = true;

  // show link labels in DesktopView
  showLinkLabel: boolean = true;
  isSideNavShown: boolean = true;

  workerId: number;

  @ViewChild('sidenav') sidenav;

  @HostListener('mousewheel')
  @HostListener('window:scroll')
  @HostListener('document:click')
  onResetLogoutCountdownInteracitonEvent() {
    if (!this.noneActiveUserMessageDialog) {
      this.logoutService.restartCountdown();
    }
  }

  constructor() {
  }

  get user(): Observable<any> {
    return this.authService.getUser();
  }
  private noneActiveUserMessageDialog: MatDialogRef<WarningDialogComponent>;

  async ngOnInit() {
    await firstValueFrom(this.authService.checkAuth());

    this.addCustomMatIcons();
    this.initNoneActiveUserMessage();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (!!event.url && window.location.href.includes('/#/')) {
          window.location.href = window.location.href.replace('/#/', '/');
        }
      }
    });

    this.subs.sink = this.authService.isAuthenticated$
      .pipe(distinctUntilChanged())
      .subscribe(async (isAuthenticated) => {
        if (isAuthenticated && this.shouldStartLogoutCountdown()) {
          document.body.classList.add('hiddenChat');
          this.logoutService.startLogoutCountDown(this.config.noActiveUserTTL);
        } else {
          document.body.classList.remove('hiddenChat');
          this.logoutService.cancelLogout();
          this.disableNoActiveUserMessage();
        }

        if (isAuthenticated && this.authService.authServerUserId) {
          this.workerId = await firstValueFrom(this.workerService.getWorkerIdByAuthServerUserId(this.authService.authServerUserId));
          this.hasAccessToRegulationsAndProcedures = this.workerId && await firstValueFrom(this.workerService.getAccessToRegulationsAndProcedures(this.workerId));
        }
      });

    this.isAdminSubscription = this.authService.isAdmin$.subscribe((status) => (this.isAdmin = status));

    this.isDesktopView = window.innerWidth >= 920;

    window.addEventListener('resize', () => {
      this.isDesktopView = window.innerWidth >= 920;
    });

    Hotjar.init(3087435, 6, {
      debug: true,
    });
  }

  private shouldStartLogoutCountdown() {
    return !this.isQrCodesAccount();
  }

  isQrCodesAccount(): boolean {
    return this.authService.hasPermission(Permission.QrCode);
  }

  private initNoneActiveUserMessage() {
    this.subs.sink = this.authService.authNavStatus$
      .pipe(
        filter(status => status),
        switchMap(_ => this.logoutService.timeToLogout$))
      .subscribe(async (time) => {
        if (time <= this.config.noActiveUserMessageTTLLimit && this.shouldStartLogoutCountdown()) {
          await this.enableNoActiveUserMessage();
        } else {
          this.disableNoActiveUserMessage();
        }
      });
  }

  async signout() {
    Hotjar.event('wylogowanie!');
    await this.authService.signout();
  }

  async signin() {
    Hotjar.event('zalogowanie!');
    await this.authService.login();
  }

  async getExpirationDate() {
    alert(new Date(this.authService.getExpirationDate()));
  }

  async register() {
    await this.authService.register();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  public onScroll(id: string): void {
    this.router.navigate(['/']).then(() => {
      const el = document.getElementById(id);
      el.scrollIntoView();
    });
  }

  toggleLinkLabels(): void {
    this.showLinkLabel = !this.showLinkLabel;
    this.cdr.markForCheck();
  }

  toggleSideNav(): void {
    if (!this.sidenav) {
      return;
    }

    this.isSideNavShown = !this.isSideNavShown;
  }

  private addCustomMatIcons(): void {
    this.matIconRegistry.addSvgIcon('bin-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/bin.svg'));
    this.matIconRegistry.addSvgIcon('close-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/close.svg'));
    this.matIconRegistry.addSvgIcon('thumb-up-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/thumb-up.svg'));
    this.matIconRegistry.addSvgIcon('thumb-down-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/thumb-down.svg'));
    this.matIconRegistry.addSvgIcon('files-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/files.svg'));
    this.matIconRegistry.addSvgIcon('agreements-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/agreements.svg'));
    this.matIconRegistry.addSvgIcon('timesheet-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/timesheet.svg'));
    this.matIconRegistry.addSvgIcon('documents-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/documents.svg'));
    this.matIconRegistry.addSvgIcon('delegations-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/delegations.svg'));
    this.matIconRegistry.addSvgIcon('absences-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/absences.svg'));
    this.matIconRegistry.addSvgIcon('legalization-status-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/legalization.svg'));
    this.matIconRegistry.addSvgIcon('employment-conditions-confirmation-status-icon', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/employment-conditions-confirmation-status.svg'));
  }

  private async enableNoActiveUserMessage() {
    if (!this.noneActiveUserMessageDialog) {
      PageTitleNotification.On(() => this.translateService.instant('NoActiveUserLogoutDialog.BrowserTitle', { time: Math.round(this.logoutService.timeToLogout / 1000) }));
      this.noneActiveUserMessageDialog = this.dialog.open(WarningDialogComponent,
        {
          panelClass: 'none-active-user-logout-dialog',
          disableClose: true,
          data: {
            message: 'NoActiveUserLogoutDialog.Title',
            closeButtonLabel: 'NoActiveUserLogoutDialog.ExtendSessions',
          },
        });

      await firstValueFrom(this.noneActiveUserMessageDialog.afterClosed());
      this.logoutService.restartCountdown();
    }
  }

  private disableNoActiveUserMessage() {
    if (this.noneActiveUserMessageDialog) {
      this.noneActiveUserMessageDialog.close();
      this.noneActiveUserMessageDialog = undefined;
      PageTitleNotification.Off();
    }
  }
}
